import moment from 'moment-timezone';
import type { Moment } from 'moment';

import { clampCalendarRangeToToday } from '@src/packages/utils/dates/disableFutureCalendarDates';

import type { AbsolutePeriodValue, PeriodMaxSpan, PeriodPresetId } from './RelativeAbsolutePeriodFilter.types';
import { clampRangeToSpan } from './periodLimit';

/** Resolves a preset to inclusive daily bounds in the report's timezone. */
export const resolvePeriodPreset = (
  preset: PeriodPresetId,
  reference: Moment = moment(),
  timeZone?: string,
  maxSpan?: PeriodMaxSpan,
  historicalOnly = false,
): [Moment, Moment] => {
  const today = (timeZone ? reference.clone().tz(timeZone) : reference.clone()).startOf('day');
  let start: Moment;
  let end = today.clone().endOf('day');

  switch (preset) {
    case 'today':
      start = today.clone();
      break;
    case 'yesterday':
      start = today.clone().subtract(1, 'day');
      end = start.clone().endOf('day');
      break;
    case 'currentWeek':
      start = today.clone().startOf('isoWeek');
      end = today.clone().endOf('isoWeek');
      break;
    case 'previousWeek':
      start = today.clone().startOf('isoWeek').subtract(1, 'week');
      end = start.clone().add(6, 'days').endOf('day');
      break;
    case 'last7Days':
      start = today.clone().subtract(6, 'days');
      break;
    case 'last30Days':
      start = today.clone().subtract(29, 'days');
      break;
    case 'currentMonth':
      start = today.clone().startOf('month');
      end = today.clone().endOf('month');
      break;
    case 'previousMonth':
      start = today.clone().subtract(1, 'month').startOf('month');
      end = start.clone().endOf('month');
      break;
    case 'last3Months':
      start = today.clone().subtract(3, 'months');
      break;
    default: {
      const exhaustive: never = preset;
      return exhaustive;
    }
  }

  const fullRange: [Moment, Moment] = [start.startOf('day'), end.endOf('day')];
  const range = historicalOnly ? clampCalendarRangeToToday(fullRange, timeZone, reference) : fullRange;
  return maxSpan ? clampRangeToSpan(range, 'day', maxSpan) : range;
};

export const createPresetPeriodValue = (
  preset: PeriodPresetId,
  reference?: Moment,
  timeZone?: string,
  maxSpan?: PeriodMaxSpan,
  historicalOnly = false,
): AbsolutePeriodValue & { dateRange: [Moment, Moment] } => ({
  isRelative: false,
  dateRange: resolvePeriodPreset(preset, reference, timeZone, maxSpan, historicalOnly),
  dateType: 'day',
  mode: 'preset',
  presetId: preset,
});
